import axios from 'axios'
import {
  ensureFreshAccessToken,
  handleSessionExpired,
  isSessionExpiryResponse,
  refreshAccessToken,
  startSessionRefreshScheduler
} from '@/utils/sessionExpiryHandler'

const INTERCEPTOR_FLAG = '__emergencyAuthInterceptorInstalled'
const RESPONSE_INTERCEPTOR_FLAG = '__emergencyAuthResponseInterceptorInstalled'
const CREATE_PATCH_FLAG = '__emergencyAxiosCreatePatched'
const BLANK_BEARER_RE = /^Bearer\s*$/i

function getToken() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return ''
  }
  try {
    return window.localStorage.getItem('token') || ''
  } catch (error) {
    return ''
  }
}

function normalizeAxiosAuthHeader(config, token = getToken()) {
  config.headers = config.headers || {}
  const currentAuth = config.headers['Authorization'] || config.headers['authorization'] || ''
  const hasBlankBearer = BLANK_BEARER_RE.test(String(currentAuth || ''))

  // 同源平台 API 始终使用最新用户 Token。续期与实际发包之间若发生轮换，
  // 这里会覆盖调用点提前写入的旧 Bearer，避免仍携带旧 Token 发出请求。
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
    delete config.headers['authorization']
    return
  }

  if (!token && hasBlankBearer) {
    delete config.headers['Authorization']
    delete config.headers['authorization']
  }
}

function isApiRequest(config = {}) {
  const combined = (config.baseURL || '') + (config.url || '')
  return combined.startsWith('/api') || combined.includes('/api/')
}

function isAuthRetrySkipped(config = {}) {
  const combined = `${config.baseURL || ''}${config.url || ''}`.toLowerCase()
  return combined.includes('/auth/login') || combined.includes('/auth/refresh')
}

function createSilentAuthError() {
  const silentError = new Error('会话已过期')
  silentError._isAuthError = true
  silentError._silent = true
  return silentError
}

function installAxiosAuthInterceptor(client) {
  if (!client || !client.interceptors || !client.interceptors.request || client[INTERCEPTOR_FLAG]) {
    return client
  }

  client.interceptors.request.use(async config => {
    const combined = (config.baseURL || '') + (config.url || '')
    if (combined.startsWith('/api') || combined.includes('/api/')) {
      const token = isAuthRetrySkipped(config)
        ? getToken()
        : await ensureFreshAccessToken()
      normalizeAxiosAuthHeader(config, token || '')
    }
    return config
  })

  client[INTERCEPTOR_FLAG] = true

  if (!client[RESPONSE_INTERCEPTOR_FLAG] && client.interceptors.response) {
    client.interceptors.response.use(
      response => response,
      async error => {
        const status = Number(error?.response?.status || 0)
        const originalConfig = error?.config || {}
        const shouldRefresh = status === 401 &&
          isApiRequest(originalConfig) &&
          !isAuthRetrySkipped(originalConfig) &&
          !originalConfig._authRetry

        if (shouldRefresh) {
          originalConfig._authRetry = true
          const refreshed = await refreshAccessToken()
          if (refreshed?.token) {
            originalConfig.headers = originalConfig.headers || {}
            originalConfig.headers.Authorization = `Bearer ${refreshed.token}`
            return client(originalConfig)
          }
        }

        if (status === 401 &&
          isSessionExpiryResponse(error) &&
          !isAuthRetrySkipped(originalConfig) &&
          !originalConfig._skipSessionExpiryPrompt) {
          await handleSessionExpired()
          return Promise.reject(createSilentAuthError())
        }

        return Promise.reject(error)
      }
    )
    client[RESPONSE_INTERCEPTOR_FLAG] = true
  }
  return client
}

function installAxiosCreateInterceptor() {
  if (axios[CREATE_PATCH_FLAG]) {
    return
  }

  const nativeCreate = axios.create.bind(axios)
  axios.create = (...args) => installAxiosAuthInterceptor(nativeCreate(...args))
  axios[CREATE_PATCH_FLAG] = true
}

// 安装全局 axios 拦截器（为直接使用 import axios 的代码提供 token 注入）
installAxiosCreateInterceptor()
installAxiosAuthInterceptor(axios)
startSessionRefreshScheduler()

/**
 * @deprecated 文档 iframe/下载必须改用 createDocumentAccessUrl() 的短时单文档票据。
 * 保留此函数仅用于防止历史动态调用点立即崩溃；不再向 URL 写入 Access Token。
 */
export function withApiAccessToken(rawUrl = '') {
  return String(rawUrl || '').trim()
}
